import { Timestamp } from "firebase/firestore";
import { File } from "./file";

export interface ProjectSummary {
  id: string;
  status: string;
  org_id: string;
  infos: {
    general: {
      name: string;
      num: string;
      city?: string;
      creation_date: Timestamp;
      photo_file?: { url: string };
      coordinates?: { latitude: number; longitude: number };
    };
    team: {
      commercial_id?: string;
      conductor_id?: string;
      comptability_id?: string;
    };
    client?: {
      contacts: ContactInfos[];
    };
  };
  last_completed_cp?: string;
  current_step?: string;
  unseen_by_user?: Record<string, { photos?: number; documents?: number; emails?: number; signatures?: number; billing_comments?: number }>;
}

export interface ProjectBillingStep {
  label: string;
  /** Short label snapshotted from the billing template at creation. */
  abbreviation?: string;
  percentage: number;
  montant: number;
  is_demarrage: boolean;
  linked_step_ids: string[];
  status: "pending" | "creating" | "draft" | "finalized" | "sent" | "paid" | "error";
  invoice_id?: number;
  invoice_number?: string;
  invoice_pdf_url?: string;
  drafted_at?: string;
  finalized_at?: string;
  sent_at?: string;
  sent_dates?: string[];
  paid_at?: string;
  invoice_error?: string;
  billing_warning?: string;
  /**
   * Legacy: written by both scheduler alerts and manual relances before
   * the field split. Read for backwards-compat fallback only — new code
   * writes either `last_conductor_alert_sent_at` or `last_relance_sent_at`.
   */
  last_alert_sent_at?: string;
  /** Set by the scheduler when a retard alert is emailed to the conducteur. */
  last_conductor_alert_sent_at?: string;
  /** Set ONLY when a human clicks "Relancer". Surfaced in the UI as
   * "Relance demandée le …". */
  last_relance_sent_at?: string;
  payment_delay_days?: number;
  project_delay_days?: number;
  step_gap_delay_days?: number;
}

export interface BillingFlags {
  has_late: boolean;
  overdue_count: number;
  has_project_delay: boolean;
  total_paid: number;
  total_invoiced: number;
  next_due_date?: string;
  last_computed_at?: string;
}

export interface BillingComment {
  id: string;
  text: string;
  author_id: string;
  author_name: string;
  author_photo_url?: string | null;
  created_at: string;
}

export interface ProjectBilling {
  billing_template_id?: string;
  montant_total: number;
  steps: ProjectBillingStep[];
  pennylane_customer_id?: number;
  planning_end_date?: string;
  billing_flags?: BillingFlags & { has_step_gap?: boolean };
  comments?: BillingComment[];
  /** ISO timestamp set when an admin manually archives this billing from
   *  the Suivi de facturation / Suivi salariés dashboards. Null when not
   *  archived. Only available for projects with status="in_progress". */
  billing_archived_at?: string | null;
}

export interface Project {
  id: string;
  infos: ProjectInfos;

  org_id: string;
  author_id: string;
  history_events: ProjectHistoryEvent[];
  themes: ProjectTheme[];
  photos: ProjectPhoto[];
  folders: ProjectFolder[];

  status: string;
  guest_user_ids?: string[];
  current_step?: string;
  last_completed_cp?: string;
  billing?: ProjectBilling;
  unseen_by_user?: Record<string, { photos?: number; documents?: number; emails?: number; signatures?: number; billing_comments?: number }>;
  /**
   * The project template the project was created from. Lets later code
   * (e.g. ModifyProjectModal applying a new billing template) resolve
   * `bt.mappings[project_template_id]` to tie billing steps to the
   * project's controlpoints. Optional: legacy projects predate this field.
   */
  project_template_id?: string;
}

export interface ProjectInfos {
  general: GeneralInfos;
  client: ClientInfos;
  team: TeamInfos;
  partners: PartnerInfos[];
}

export interface GeneralInfos {
  name: string;
  num: string;
  address?: string;
  city?: string;
  postal?: string;
  country?: string;
  coordinates?: {
    latitude: number;
    longitude: number;
  };
  home_address?: string;
  home_city?: string;
  home_postal?: string;
  lot?: string;
  section?: string;
  cadastre_number?: string;

  creation_date: Timestamp;
  last_updated: Timestamp;
  photo_file?: File;
  notes?: string;
}

export interface ClientInfos {
  contacts: ContactInfos[];
  last_activity_date?: Timestamp;
}
export interface ContactInfos {
  first_name: string;
  last_name: string;
  prefix: "M." | "Mme" | "SCI" | "SAS" | "SA" | "SARL";
  phone_number?: string;
  email?: string;
  user_id?: string;
  // Company name for company-type contacts (SCI/SAS/SA/SARL). Field kept named
  // `sci_name` for backwards compatibility — it holds the other forms too.
  sci_name?: string;
  access_status?: "no_email" | "not_invited" | "pending" | "active";
  last_active?: Timestamp;
}

/** Civility prefixes that designate a company (need a company name, not a
 *  first/last name). All company forms store their name in `sci_name`. */
export const COMPANY_PREFIXES = ["SCI", "SAS", "SA", "SARL"] as const;
export const isCompanyPrefix = (prefix?: string | number): boolean =>
  COMPANY_PREFIXES.includes(prefix as (typeof COMPANY_PREFIXES)[number]);

export interface TeamInfos {
  commercial_id?: string;
  conductor_id?: string;
  dessinateur_id?: string;
  sav_id?: string;
  comptability_id?: string;
  communication_id?: string;
  visible_member_ids?: string[];
}
export interface PartnerDecennale {
  start_date: string; // ISO date "2025-03-15"
  end_date: string;   // ISO date "2026-03-14"
  gcs_path: string;
  url?: string;
  /** @deprecated Legacy field — use start_date/end_date instead */
  year?: number;
}

export interface PartnerInfos {
  id: string;
  display_name: string;
  phone_number?: string;
  email?: string;
  company_name?: string;
  company_phone?: string;
  company_address?: string;
  company_city?: string;
  company_postal?: string;
  company_email?: string;
  decennales?: PartnerDecennale[];
  company_types: string[];
}

export interface ProjectHistoryEvent {
  id: string;
  date: Timestamp;
  user: {
    id: string;
    display_name: string;
    photo_url?: string;
  };
  action: string;
  photos_id?: string[]; // Array of photo URLs or IDs related to the action
  documents_id?: string[]; // Array of documents related to the action
  control_point?: {
    id: string;
    name: string;
    new_status: string;
  };
  email?: {
    email_sent_id: string;
    template_id: string;
    sent: boolean;
    subject: string;
    body: string;
  };
  signature_request_id?: string;
}

export type ProjectTheme = {
  id: string;
  label: string;
  steps: Step[];
};

export type Step = {
  id: string;
  label: string;
  controlpoints: ControlPoint[];
};

export type ControlPoint = {
  id: string;
  label: string;
  state: "default" | "completed" | "missed" | "not_applicable";
  photos_id?: string[]; // Array of photo URLs or IDs related to the control point
  documents_id?: string[]; // Array of documents related to the control point
  associated_email_template_id?: string; // Optional field for the email template associated with the control point
  email?: {
    sent?: boolean; // Optional field to indicate if the email has been sent for this control point
    date?: Timestamp; // Optional field to store the date when the email was sent for this control point
    email_sent_id?: string; // Optional field to store the ID of the sent email related to this control point
  };
  hidden?: boolean; // If true, hidden from guest/client view
  /**
   * Admin-chosen accent color (hex). Overrides the auto-colors (orange =
   * email sent, blue = partner template) in the Étapes tab.
   */
  color?: string;
};

export interface ProjectPhoto {
  id: string;
  file: File;
  description?: string;
  geo_location?: {
    latitude: number;
    longitude: number;
  };
  hidden?: boolean;
}

export interface ProjectFolder {
  id: string;
  name: string;
  documents?: ProjectDocument[];
  sub_folders?: ProjectSubFolder[];
  /**
   * When true, the project's conducteur receives a push notification each
   * time a document is uploaded into this folder.
   */
  notify_conductor_on_upload?: boolean;
}

export interface ProjectSubFolder {
  id: string;
  name: string;
  documents?: ProjectDocument[];
  /**
   * When true, the project's conducteur receives a push notification each
   * time a document is uploaded into this subfolder.
   */
  notify_conductor_on_upload?: boolean;
}
export interface ProjectDocument {
  id: string;
  name: string;
  file: File;
  hidden?: boolean;
}

// create a Template type that has the same structure as Project but with a few modifications
export interface Template extends Project {
  order?: number;
}

/**
 * Build the display name for a project from its client contacts.
 * Each contact contributes their own full name ("LASTNAME Firstname").
 * SCI: "SCI Alpha - DUPONT Jean, MARTIN Marie"
 * Non-SCI: "DUPONT Jean, MARTIN Marie"
 * Fallback: project general name
 */
export const getProjectDisplayName = (project: { infos: { general: { name: string }; client?: { contacts: ContactInfos[] } } }): string => {
  const contacts = project.infos.client?.contacts;
  if (!contacts || contacts.length === 0) return project.infos.general.name;

  const firstContact = contacts[0];
  const isCompany = isCompanyPrefix(firstContact.prefix) && firstContact.sci_name;

  // Build full contact names: "DUPONT Jean, MARTIN Marie, ..."
  const contactPart = contacts
    .map((c) => `${c.last_name || ""} ${c.first_name || ""}`.trim())
    .filter(Boolean)
    .join(", ");

  if (!contactPart) return project.infos.general.name;

  return isCompany
    ? `${firstContact.prefix} ${firstContact.sci_name} - ${contactPart}`
    : contactPart;
};
